import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Quote, Play, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `/api`;

const parseVideoUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const host = urlObj.hostname.replace('www.', '');
    if (host.includes('youtube.com')) {
      const videoId = urlObj.searchParams.get('v');
      if (videoId) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}`, originalUrl: url };
    }
    if (host.includes('youtu.be')) {
      const videoId = urlObj.pathname.slice(1);
      if (videoId) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}`, originalUrl: url };
    }
    if (host.includes('instagram.com')) {
      const match = urlObj.pathname.match(/\/(reel|p)\/([A-Za-z0-9_-]+)/);
      if (match) return { type: 'instagram', embedUrl: `https://www.instagram.com/${match[1]}/${match[2]}/embed/`, originalUrl: url };
    }
  } catch { /* invalid url */ }
  return { type: 'other', embedUrl: url, originalUrl: url };
};

const TransformationModal = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose} data-testid="transformation-modal-overlay">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#1A1A1A] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
        onClick={(e) => e.stopPropagation()} data-testid="transformation-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-xl font-black text-white">{item.name}</h3>
            {item.transformation_title && (
              <span className="inline-block bg-[#FED601] text-black text-xs font-bold uppercase px-3 py-1 rounded mt-1">{item.transformation_title}</span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="close-transformation-modal"><X size={24} /></button>
        </div>
        <div className="p-6">
          {(item.before_image || item.after_image) && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-red-400 mb-2 block">Antes</span>
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-black">
                  {item.before_image ? <img src={item.before_image} alt={`${item.name} - Antes`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">Sem imagem</div>}
                </div>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-green-400 mb-2 block">Depois</span>
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-black">
                  {item.after_image ? <img src={item.after_image} alt={`${item.name} - Depois`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">Sem imagem</div>}
                </div>
              </div>
            </div>
          )}
          {item.video_url && (() => {
            const video = parseVideoUrl(item.video_url);
            if (!video) return null;
            return (
              <div className="mb-6">
                <div className={`rounded-xl overflow-hidden bg-black ${video.type === 'instagram' ? 'max-w-md mx-auto' : 'aspect-video'}`}>
                  <iframe src={video.embedUrl} title={`${item.name} - Vídeo`} className={`w-full ${video.type === 'instagram' ? 'min-h-[500px]' : 'h-full'}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="transformation-video" />
                </div>
                {video.type === 'instagram' && (
                  <a href={video.originalUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white px-5 py-2.5 rounded-lg font-bold text-sm hover:opacity-90 transition-opacity" data-testid="view-on-instagram-link"><ExternalLink size={16} /> Ver no Instagram</a>
                )}
              </div>
            );
          })()}
          {item.results_description && (
            <div className="bg-[#252525] rounded-xl p-5 mb-6 border border-[#FED601]/20">
              <h4 className="text-[#FED601] font-bold text-sm uppercase tracking-widest mb-3">Resultados</h4>
              <p className="text-gray-300 whitespace-pre-line leading-relaxed">{item.results_description}</p>
            </div>
          )}
          {item.text && (
            <div className="bg-[#252525] rounded-xl p-5 border border-white/5">
              <Quote size={20} className="text-[#FED601]/40 mb-2" />
              <p className="text-gray-300 italic leading-relaxed">"{item.text}"</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Transformations = () => {
  const [transformations, setTransformations] = useState([]);
  const [selectedTransformation, setSelectedTransformation] = useState(null);
  const [page, setPage] = useState(0);
  const perPage = 6;

  useEffect(() => {
    axios.get(`${API}/content/testimonials/transformations`)
      .then((res) => { if (res.data?.length > 0) setTransformations(res.data); })
      .catch(() => {});
  }, []);

  if (transformations.length === 0) return null;

  const totalPages = Math.ceil(transformations.length / perPage);
  const visible = transformations.slice(page * perPage, page * perPage + perPage);
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <>
      <section id="testemunhos" className="py-20 lg:py-28 bg-[#FAF8F5]" data-testid="transformations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">Resultados Comprovados</span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1A1A1A] mt-2 mb-4">Transformações <span className="text-[#FED601]">Reais</span></h2>
          </motion.div>

          <div className="relative">
            {canPrev && (
              <button onClick={() => setPage(p => p - 1)} aria-label="Transformações anteriores" className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-[#E0DDD7] shadow-lg flex items-center justify-center text-[#1A1A1A] hover:border-[#FED601] hover:text-[#FED601] transition-colors" data-testid="transformations-prev-btn"><ChevronLeft size={24} /></button>
            )}
            {canNext && (
              <button onClick={() => setPage(p => p + 1)} aria-label="Transformações seguintes" className="absolute -right-3 md:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-[#E0DDD7] shadow-lg flex items-center justify-center text-[#1A1A1A] hover:border-[#FED601] hover:text-[#FED601] transition-colors" data-testid="transformations-next-btn"><ChevronRight size={24} /></button>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
              {visible.map((item, index) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-white rounded-xl overflow-hidden border border-[#E0DDD7] hover:border-[#FED601]/50 hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => setSelectedTransformation(item)} data-testid={`transformation-card-${item.id}`}>
                  <div className="relative h-44 md:h-64 overflow-hidden">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FED601?text=Justfit'; }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
                    {item.transformation_title && (
                      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                        <span className="inline-block bg-[#FED601] text-black px-3 py-1.5 font-bold text-xs uppercase rounded">{item.transformation_title}</span>
                        {item.video_url && <span className="inline-flex items-center bg-white/90 text-black px-1.5 py-1.5 rounded" title="Tem vídeo"><Play size={12} className="fill-black" /></span>}
                      </div>
                    )}
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(parseInt(item.rating) || 5)].map((_, i) => (<Star key={i} size={14} className="text-[#FED601] fill-[#FED601]" />))}
                    </div>
                    <p className="text-[#4A4A4A] italic text-sm mb-2 line-clamp-2">"{item.text}"</p>
                    <p className="text-[#1A1A1A] font-bold text-sm">{item.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button key={i} onClick={() => setPage(i)} aria-label={`Página ${i + 1} de transformações`} className={`h-2.5 rounded-full transition-all ${page === i ? 'bg-[#FED601] w-6' : 'bg-[#E0DDD7] w-2.5'}`} data-testid={`transformation-page-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedTransformation && <TransformationModal item={selectedTransformation} onClose={() => setSelectedTransformation(null)} />}
      </AnimatePresence>
    </>
  );
};
